// Storage durability (ARCHITECTURE.md §11).
//
// Browser storage is best-effort by default: IndexedDB can be evicted under
// disk pressure. Requesting PERSISTENT storage exempts the origin from
// automatic eviction — it is then only cleared if the user explicitly clears
// site data. Grant depends on browser heuristics (installed PWA, engagement,
// notification permission); some browsers prompt, some auto-grant.
//
// IMPORTANT: this is a strong mitigation, NOT a guarantee. iOS Safari caps
// script-writable storage for sites not added to the Home Screen, and the user
// can always clear data. That is exactly why the recovery phrase is the source
// of truth — persistence protects the convenience copy, the seed protects the
// identity.

export interface StorageStatus {
  persisted: boolean;
  supported: boolean;
  usage?: number;
  quota?: number;
}

let latest: StorageStatus = { persisted: false, supported: true };

/** Last known storage status (from the most recent ensurePersistentStorage). */
export function lastStorageStatus(): StorageStatus {
  return latest;
}

/**
 * Ensure the origin has persistent storage, requesting it if needed.
 * Idempotent and safe to call on every startup.
 */
export async function ensurePersistentStorage(): Promise<StorageStatus> {
  const storage = navigator.storage;
  if (!storage || typeof storage.persisted !== 'function') {
    return { persisted: false, supported: false };
  }

  let persisted = await storage.persisted();
  if (!persisted && typeof storage.persist === 'function') {
    try {
      persisted = await storage.persist();
    } catch {
      persisted = false;
    }
  }

  const status: StorageStatus = { persisted, supported: true };
  if (typeof storage.estimate === 'function') {
    try {
      const { usage, quota } = await storage.estimate();
      if (usage !== undefined) status.usage = usage;
      if (quota !== undefined) status.quota = quota;
    } catch {
      /* estimate is optional */
    }
  }
  latest = status;
  return status;
}
